import { createBrowserRouter, RouterProvider, Outlet, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import ProtectedRoute from "./ProtectedRoute";
import PageLoader from "../components/common/PageLoader";
import Sidebar from "../components/layout/Sidebar";
import Footer from "../components/layout/Footer";
import LoginPage from "../pages/Login/LoginPage"; // eager — first page, no flash

/** Auto-reload on ChunkLoadError (stale cache after dev-server rebuild) */
const lazyWithRetry = (factory) =>
  lazy(() =>
    factory().catch(() => {
      window.location.reload();
      return new Promise(() => {}); // keep fallback until reload
    })
  );

/**
 * Shared layout: Sidebar + page content + Footer.
 * Mounted ONCE for all authenticated routes — sidebar collapsed state persists on navigation.
 * Suspense is scoped to Outlet only — Sidebar/Footer stay visible while lazy chunks load.
 */
const Layout = () => (
  <>
    <Sidebar />
    <Suspense fallback={<PageLoader message="Loading page…" />}>
      <Outlet />
    </Suspense>
    <Footer />
  </>
);

const DashboardPage            = lazyWithRetry(() => import("../pages/Dashboard/DashboardPage"));
const InvoicePage              = lazyWithRetry(() => import("../pages/Invoice/InvoicePage"));
const EditInvoicePage          = lazyWithRetry(() => import("../pages/Invoice/EditInvoicePage"));
const GenerateInvoicePdfPage   = lazyWithRetry(() => import("../pages/Invoice/GenerateInvoicePdfPage"));
const ConsigneeDetailsGridPage = lazyWithRetry(() => import("../pages/ConsigneeDetails/ConsigneeDetailsGridPage"));
const ConsigneeDetailsFormPage = lazyWithRetry(() => import("../pages/ConsigneeDetails/ConsigneeDetailsFormPage"));
const InvoiceGridPage          = lazyWithRetry(() => import("../pages/Invoice/InvoiceGridPage"));
const UserManagementGridPage   = lazyWithRetry(() => import("../pages/UserManagement/UserManagementGridPage"));
const UserManagementFormPage   = lazyWithRetry(() => import("../pages/UserManagement/UserManagementFormPage"));
const ProductManagementPage    = lazyWithRetry(() => import("../pages/ProductManagement/ProductManagementPage"));
const GSTReportsPage           = lazyWithRetry(() => import("../pages/Reports/GSTReportsPage"));
const PaymentTrackerPage       = lazyWithRetry(() => import("../pages/Reports/PaymentTrackerPage"));
const QuotationGridPage        = lazyWithRetry(() => import("../pages/Quotation/QuotationGridPage"));
const QuotationFormPage        = lazyWithRetry(() => import("../pages/Quotation/QuotationFormPage"));
const OrderCopyGridPage        = lazyWithRetry(() => import("../pages/OrderCopy/OrderCopyGridPage"));
const OrderCopyFormPage        = lazyWithRetry(() => import("../pages/OrderCopy/OrderCopyFormPage"));
const JobOrderGridPage         = lazyWithRetry(() => import("../pages/JobOrder/JobOrderGridPage"));
const JobOrderFormPage         = lazyWithRetry(() => import("../pages/JobOrder/JobOrderFormPage"));
const DispatchGridPage         = lazyWithRetry(() => import("../pages/Dispatch/DispatchGridPage"));
const DispatchFormPage         = lazyWithRetry(() => import("../pages/Dispatch/DispatchFormPage"));
const CustomerStatementPage    = lazyWithRetry(() => import("../pages/CustomerStatement/CustomerStatementPage"));
const ChallanGridPage          = lazyWithRetry(() => import("../pages/Challan/ChallanGridPage"));
const ChallanFormPage          = lazyWithRetry(() => import("../pages/Challan/ChallanFormPage"));
const RawMaterialGridPage      = lazyWithRetry(() => import("../pages/RawMaterial/RawMaterialGridPage"));
const RawMaterialFormPage      = lazyWithRetry(() => import("../pages/RawMaterial/RawMaterialFormPage"));
const ExpensesPage             = lazyWithRetry(() => import("../pages/Expenses/ExpensesPage"));

/** Roles allowed per section */
const ALL_ROLES        = ["admin", "superadmin"];
const SUPERADMIN_ONLY  = ["superadmin"];

const basename = '/';  //process.env.PUBLIC_URL || "/";

const guard = (element, roles, pageKey) => (
  <ProtectedRoute requiredRoles={roles} pageKey={pageKey}>{element}</ProtectedRoute>
);

const router = createBrowserRouter(
  [
    { path: "/",      element: <LoginPage /> },
    { path: "/login", element: <LoginPage /> }, // alias
    {
      element: <Layout />,
      children: [
        { path: "/dashboard",                  element: guard(<DashboardPage />,             ALL_ROLES,       "dashboard") },
        { path: "/invoice",                    element: guard(<InvoicePage />,               ALL_ROLES,       "invoice") },
        { path: "/editInvoice/:id/:timeStamp", element: guard(<EditInvoicePage />,           ALL_ROLES,       "invoice") },
        { path: "/pdfInvoice/:id/:timeStamp",  element: guard(<GenerateInvoicePdfPage />,    ALL_ROLES,       "invoice") },
        { path: "/invoiceGrid",                element: guard(<InvoiceGridPage />,           ALL_ROLES,       "invoiceGrid") },
        { path: "/cdGrid",                     element: guard(<ConsigneeDetailsGridPage />,  ALL_ROLES,       "consigneeDetails") },
        { path: "/cdForm/:id?",                element: guard(<ConsigneeDetailsFormPage />,  ALL_ROLES,       "consigneeDetails") },
        { path: "/users",                      element: guard(<UserManagementGridPage />,    ALL_ROLES,       "users") },
        { path: "/userForm/:id?",              element: guard(<UserManagementFormPage />,    ALL_ROLES,       "users") },
        { path: "/productManagement",          element: guard(<ProductManagementPage />,     ALL_ROLES,       "productManagement") },
        { path: "/gstReports",                 element: guard(<GSTReportsPage />,            ALL_ROLES,       "gstReports") },
        { path: "/paymentTracker",             element: guard(<PaymentTrackerPage />,        ALL_ROLES,       "paymentTracker") },
        { path: "/quotations",                 element: guard(<QuotationGridPage />,         ALL_ROLES,       "quotations") },
        { path: "/quotationForm/:id?",         element: guard(<QuotationFormPage />,         ALL_ROLES,       "quotations") },
        { path: "/ordercopy",                 element: guard(<OrderCopyGridPage />,         ALL_ROLES,       "ordercopy") },
        { path: "/ordercopyForm/:id?",         element: guard(<OrderCopyFormPage />,         ALL_ROLES,       "ordercopy") },
        { path: "/jobOrders",                  element: guard(<JobOrderGridPage />,          ALL_ROLES,       "jobOrders") },
        { path: "/jobOrderForm/:id?",          element: guard(<JobOrderFormPage />,          ALL_ROLES,       "jobOrders") },
        { path: "/dispatches",                 element: guard(<DispatchGridPage />,          ALL_ROLES,       "dispatches") },
        { path: "/dispatchForm/:id?",          element: guard(<DispatchFormPage />,          ALL_ROLES,       "dispatches") },
        { path: "/customerStatement",          element: guard(<CustomerStatementPage />,     ALL_ROLES,       "customerStatement") },
        { path: "/challans",                   element: guard(<ChallanGridPage />,           ALL_ROLES,       "challans") },
        { path: "/challanForm/:id?",           element: guard(<ChallanFormPage />,           ALL_ROLES,       "challans") },
        { path: "/rawMaterials",               element: guard(<RawMaterialGridPage />,       ALL_ROLES,       "rawMaterials") },
        { path: "/rawMaterialForm/:id?",       element: guard(<RawMaterialFormPage />,       ALL_ROLES,       "rawMaterials") },
        { path: "/expenses",                   element: guard(<ExpensesPage />,              ALL_ROLES,       "expenses") },
      ],
    },
    { path: "*", element: <Navigate to="/" replace /> }, // catch-all → login
  ],
  { basename },
);

const AppRouter = () => {
  return <RouterProvider router={router} />;
};

export default AppRouter;
